import type {
  BlockSelection,
  EditorHost,
  TextSelection,
} from '@blocksuite/block-std';
import type {
  DocMode,
  EdgelessRootService,
  ImageSelection,
  PageRootService,
} from '@blocksuite/blocks';
import { NoteDisplayMode } from '@blocksuite/blocks';
import {
  Bound,
  getElementsBound,
  type SerializedXYWH,
} from '@blocksuite/global/utils';
import type { ChatMessage } from '@blocksuite/presets';
import type { Doc } from '@blocksuite/store';
import { ChatHistoryOrder } from '@pique/graphql';
import { ensureDocLoaded } from '@toeverything/infra';
import { html, type TemplateResult } from 'lit';

import { AIProvider, type AIUserInfo } from '../provider';
import { reportResponse } from '../utils/action-reporter';
import { insertFromMarkdown } from '../utils/markdown-utils';
import { CreateIcon } from './icons';

const SayMoreIcon = html`<svg
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="currentColor"
  xmlns="http://www.w3.org/2000/svg"
  style="color: var(--affine-brand-color)"
>
  <path
    d="M1.25,11.567 C1.25,5.838 6.095,1.25 12,1.25 C17.905,1.25 22.75,5.838 22.75,11.567 C22.75,17.295 17.905,21.883 12,21.883 C11.304,21.884 10.61,21.82 9.927,21.692 C9.69,21.647 9.539,21.619 9.427,21.604 C9.378,21.597 9.348,21.594 9.332,21.593 C9.315,21.6 9.282,21.613 9.228,21.639 C9.113,21.694 8.961,21.774 8.727,21.899 C7.295,22.66 5.625,22.93 4.014,22.63 C3.754,22.582 3.538,22.4 3.447,22.152 C3.356,21.903 3.402,21.625 3.569,21.42 C4.037,20.844 4.359,20.151 4.501,19.405 C4.539,19.2 4.452,18.921 4.185,18.649 C2.37,16.806 1.25,14.314 1.25,11.567 Z M12,2.75 C6.86,2.75 2.75,6.729 2.75,11.567 C2.75,13.896 3.697,16.017 5.253,17.597 C5.727,18.078 6.136,18.829 5.975,19.684 L5.975,19.685 C5.871,20.231 5.694,20.757 5.45,21.248 C6.344,21.227 7.229,20.996 8.023,20.574 L8.038,20.566 L8.042,20.564 C8.254,20.451 8.436,20.354 8.584,20.284 C8.728,20.216 8.908,20.138 9.103,20.108 C9.293,20.079 9.477,20.096 9.631,20.118 C9.784,20.139 9.972,20.174 10.186,20.214 L10.204,20.217 C10.796,20.329 11.397,20.384 11.999,20.383 C17.139,20.383 21.25,16.405 21.25,11.567 C21.25,6.729 17.14,2.75 12,2.75 Z M15.99,11 L15.999,11 C16.552,11 16.999,11.448 16.999,12 C16.999,12.552 16.552,13 15.999,13 L15.99,13 C15.438,13 14.99,12.552 14.99,12 C14.99,11.448 15.438,11 15.99,11 Z M10.996,12 C10.996,11.448 11.444,11 11.996,11 L12.005,11 C12.557,11 13.005,11.448 13.005,12 C13.005,12.552 12.557,13 12.005,13 L11.996,13 C11.444,13 10.996,12.552 10.996,12 Z M7,12 C7,11.448 7.448,11 8,11 L8.009,11 C8.561,11 9.009,11.448 9.009,12 C9.009,12.552 8.561,13 8.009,13 L8,13 C7.448,13 7,12.552 7,12 Z"
  />
</svg>`;

type Selections = {
  text?: TextSelection;
  blocks?: BlockSelection[];
  images?: ImageSelection[];
};

export type ChatAction = {
  icon: TemplateResult<1>;
  title: string;
  toast?: string;
  showWhen: (host: EditorHost) => boolean;
  handler: (
    host: EditorHost,
    content: string,
    currentSelections: Selections,
    chatSessionId?: string,
    messageId?: string
  ) => Promise<boolean>;
};

export async function queryHistoryMessages(
  workspaceId: string,
  docId: string,
  forkSessionId: string
): Promise<ChatMessage[]> {
  const histories = await AIProvider.histories?.chats(workspaceId, docId, {
    sessionId: forkSessionId,
    messageOrder: ChatHistoryOrder.asc,
  });

  if (!histories || !histories.length) {
    return [];
  }

  const firstHistory = histories[0];
  if (!firstHistory) {
    return [];
  }

  return firstHistory.messages as ChatMessage[];
}

export type FilteredChatMessage = ChatMessage & {
  userId?: string;
  userName?: string;
  avatarUrl?: string;
};

export function constructUserInfoWithMessages(
  messages: ChatMessage[],
  userInfo: AIUserInfo | null
): FilteredChatMessage[] {
  return messages
    .filter(message => message.role === 'user' || message.role === 'assistant')
    .map(message => {
      const { role, id, content, createdAt } = message;
      const isUser = role === 'user';
      const userInfoProps = isUser
        ? {
            userId: userInfo?.id,
            userName: userInfo?.name,
            avatarUrl: userInfo?.avatarUrl ?? undefined,
          }
        : {};
      return {
        id,
        role,
        content,
        createdAt,
        attachments: [] as string[],
        ...userInfoProps,
      } as FilteredChatMessage;
    });
}

export async function constructRootChatBlockMessages(
  doc: Doc,
  forkSessionId: string
): Promise<FilteredChatMessage[]> {
  const userInfo = await AIProvider.userInfo;
  const forkMessages = await queryHistoryMessages(
    doc.collection.id,
    doc.id,
    forkSessionId
  );
  return constructUserInfoWithMessages(forkMessages, userInfo);
}

function getViewportCenter(
  mode: DocMode,
  rootService: PageRootService | EdgelessRootService
) {
  const center = { x: 400, y: 50 };
  if (mode === 'page') {
    const viewport = rootService.editPropsStore.getStorage('viewport');
    if (viewport) {
      if ('xywh' in viewport) {
        const bound = Bound.deserialize(viewport.xywh);
        center.x = bound.x + bound.w / 2;
        center.y = bound.y + bound.h / 2;
      } else {
        center.x = viewport.centerX;
        center.y = viewport.centerY;
      }
    }
  } else {
    // Else we should get latest viewport center from the edgeless root service
    const edgelessService = rootService as EdgelessRootService;
    center.x = edgelessService.viewport.centerX;
    center.y = edgelessService.viewport.centerY;
  }

  return center;
}

export function promptDocTitle(host: EditorHost, autofill?: string) {
  const notification =
    host.std.spec.getService('affine:page').notificationService;
  if (!notification) return Promise.resolve(undefined);

  return notification.prompt({
    title: 'Create linked doc',
    message: 'Enter a title for the new doc.',
    placeholder: 'Untitled',
    autofill,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });
}

const ADD_TO_EDGELESS_AS_NOTE = {
  icon: CreateIcon,
  title: 'Add Sticky Note',
  showWhen: () => true,
  toast: 'New note created',
  handler: async (host: EditorHost, content: string) => {
    reportResponse('result:add-note');
    const { doc } = host;
    const service = host.spec.getService<EdgelessRootService>('affine:page');
    const elements = service.selection.selectedElements;

    const props: { displayMode: NoteDisplayMode; xywh?: SerializedXYWH } = {
      displayMode: NoteDisplayMode.EdgelessOnly,
    };

    if (elements.length > 0) {
      const bound = getElementsBound(
        elements.map(e => Bound.deserialize(e.xywh))
      );
      const newBound = new Bound(bound.x, bound.maxY + 10, bound.w);
      props.xywh = newBound.serialize();
    }

    const id = doc.addBlock('affine:note', props, doc.root?.id);

    await insertFromMarkdown(host, content, doc, id, 0);

    service.selection.set({
      elements: [id],
      editing: false,
    });

    return true;
  },
};

const CREATE_AS_LINKED_DOC = {
  icon: CreateIcon,
  title: 'Create as a linked doc',
  showWhen: () => true,
  toast: 'New doc created',
  handler: async (host: EditorHost, content: string) => {
    reportResponse('result:add-page');

    const { doc } = host;
    const surfaceBlock = doc
      .getBlocks()
      .find(block => block.flavour === 'affine:surface');
    if (!surfaceBlock) {
      return false;
    }

    const service = host.spec.getService<EdgelessRootService>('affine:page');
    const mode = service.docModeService.getMode();
    if (mode !== 'edgeless') {
      return false;
    }

    // Create a new doc and add the content to it
    const createdDoc = host.doc.collection.createDoc();
    if (!createdDoc) {
      console.error('[ChatActions] Failed to create doc');
      return false;
    }
    const newDoc = ensureDocLoaded(createdDoc);
    const rootId = newDoc.addBlock('affine:page');
    newDoc.addBlock('affine:surface', {}, rootId);
    const noteId = newDoc.addBlock('affine:note', {}, rootId);
    await insertFromMarkdown(host, content, newDoc, noteId, 0);

    // Add a linked doc card to link to the new doc
    const elements = service.selection.selectedElements;
    const width = 364;
    const height = 390;
    let x = 0;
    let y = 0;
    if (elements.length) {
      // Calculate the bound of the selected elements first
      const bound = getElementsBound(
        elements.map(e => Bound.deserialize(e.xywh))
      );
      x = bound.x;
      y = bound.y + bound.h + 100;
    }

    // If the selected elements are not in the viewport, center the linked doc card
    if (x === Number.POSITIVE_INFINITY || y === Number.POSITIVE_INFINITY) {
      const viewportCenter = getViewportCenter(mode, service);
      x = viewportCenter.x - width / 2;
      y = viewportCenter.y - height / 2;
    }

    service.addBlock(
      'affine:embed-linked-doc',
      {
        xywh: `[${x}, ${y}, ${width}, ${height}]`,
        style: 'vertical',
        pageId: newDoc.id,
      },
      surfaceBlock.id
    );

    return true;
  },
};

const SAY_MORE_ACTION: ChatAction = {
  icon: SayMoreIcon,
  title: 'Say more',
  showWhen: () => true,
  handler: async (
    _host: EditorHost,
    _content: string,
    _currentSelections: Selections,
    _chatSessionId?: string,
    _messageId?: string
  ) => {
    const panelMessages = document.querySelector('chat-panel-messages') as any;
    if (panelMessages && panelMessages._sayMore) {
      await panelMessages._sayMore();
      return true;
    }
    return false;
  },
};

const CommonActions: ChatAction[] = [
  SAY_MORE_ACTION,
  // SHARE_KEY_INSIGHTS_ACTION,
];

export const PageEditorActions = [
  ...CommonActions,
  // INSERT_BELOW,
  // CREATE_AS_DOC,
  // TODO: add this back when we have fixed affine:embed-ai-chat block from blocksuite to show 'Pique AI'
  // SAVE_CHAT_TO_BLOCK_ACTION,
];

export const EdgelessEditorActions = [
  ...CommonActions,
  // ADD_TO_EDGELESS_AS_NOTE,
  // SAVE_CHAT_TO_BLOCK_ACTION,
];

export const ChatBlockPeekViewActions = [
  ADD_TO_EDGELESS_AS_NOTE,
  CREATE_AS_LINKED_DOC,
];
